import { Stack } from "@mui/material";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { Button } from "@src/appV2/redesign/components/Button";
import { Illustration, Text } from "@src/appV2/redesign/system/ui-components";
import { getYear, isThisYear, parse } from "date-fns";
import { groupBy } from "lodash";
import { useMemo, useRef } from "react";
import { GroupedVirtuoso } from "react-virtuoso";

import { formatDayOfMonthWithDayName } from "../../../utils/formatDayOfMonthWithDayName";
import { type PaymentsListItem } from "../types/PaymentsListItem";
import { PaymentsListHeader } from "./PaymentsListHeader";
import { PaymentsListItemComponent } from "./PaymentsListItemComponent";
import { PaymentsListLoader } from "./PaymentsListLoader";

export interface PaymentsListProps {
  items: Array<PaymentsListItem & { dateGroup: string }>;
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => Promise<void>;
  onFindShiftsClick: () => void;
  onPaymentClick: (item: PaymentsListItem) => void;
}

export function PaymentsList(props: PaymentsListProps) {
  const {
    items,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
    onFindShiftsClick,
    onPaymentClick,
  } = props;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { groupLabels, groupCounts } = useMemo(() => {
    // Group by date
    const grouped = groupBy(items, (item) => item.dateGroup);

    const groupLabels = Object.keys(grouped);
    const groupCounts = groupLabels.map((label) => grouped[label].length);

    return { groupLabels, groupCounts };
  }, [items]);

  return (
    <Stack sx={{ flex: 1 }}>
      <PullToRefresh scrollRef={scrollRef} onRefresh={refetch} />
      <GroupedVirtuoso
        scrollerRef={(element) => {
          if (scrollRef.current !== element && element instanceof HTMLDivElement) {
            scrollRef.current = element;
          }
        }}
        groupCounts={groupCounts}
        groupContent={(index) => (
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.background.tertiary,
              paddingLeft: theme.spacing(5),
              paddingTop: theme.spacing(5),
              paddingBottom: theme.spacing(3),
            })}
            data-testid={`date-group-${groupLabels[index]}`}
          >
            <Text semibold variant="body2" color={(theme) => theme.palette.text.tertiary}>
              {formatGroupDate(parse(groupLabels[index], "yyyy-MM-dd", new Date()))}
            </Text>
          </Stack>
        )}
        itemContent={(index) => {
          return <PaymentsListItemComponent item={items[index]} onClick={onPaymentClick} />;
        }}
        components={{
          // if this is an inline function, it will be REMOUNTED on every render...
          Header: groupCounts.length > 0 ? PaymentsListHeader : undefined,
          Footer: hasNextPage ? PaymentsListLoader : undefined,
          EmptyPlaceholder: isLoading
            ? PaymentsListLoader
            : () => {
                return (
                  <Stack
                    spacing={6}
                    sx={(theme) => ({
                      display: "flex",
                      paddingX: theme.spacing(4),
                      height: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      bottom: "50px",
                    })}
                    data-testid="payments-empty-state"
                  >
                    <>
                      <Illustration type="pay" data-testid="illustration-pay" />
                      <Text variant="h3" sx={{ textAlign: "center" }}>
                        Book your first shift to start earning with Clipboard
                      </Text>
                      <Button fullWidth onClick={onFindShiftsClick}>
                        Find a shift
                      </Button>
                    </>
                  </Stack>
                );
              },
        }}
        endReached={() => {
          if (hasNextPage) {
            fetchNextPage();
          }
        }}
      />
    </Stack>
  );
}

function formatGroupDate(date: Date): string {
  let result = formatDayOfMonthWithDayName(date);

  if (!isThisYear(date)) {
    result += `, ${getYear(date)}`;
  }

  return result;
}
